import { PHOTO_EDITOR_CONFIG } from './photo-editor.config';
import type Cropper from 'cropperjs';
import {
	AxisBounds,
	CanvasSelectionRect, Matrix2D, MatrixScale,
	PhotoEditorAutoTrimConfig,
	PhotoEditorCropperFacade,
	PhotoEditorImageFrame,
	PhotoEditorStretchDragInput,
	PhotoEditorStretchHandle,
	PhotoEditorStretchHandlePoint, TransformMatrix2D,
	TrimAnalysisSample,
	VectorAxis
} from './photo-editor.types';

/**
 * Detects non-empty content bounds in the source image and returns them in natural image coordinates.
 * Returns `null` when no meaningful content is detected (unless `fullImageWhenEmpty` is `true`).
 */
export function resolveTrimContentNatural(
	htmlImg: HTMLImageElement,
	nw: number,
	nh: number,
	autoTrim: PhotoEditorAutoTrimConfig,
	minContentDimPx: number,
	fullImageWhenEmpty: boolean
): AxisBounds | null {
	const sampled: TrimAnalysisSample | null = sampleImageForTrimAnalysis(htmlImg, autoTrim);

	if (!sampled) {
		return fullImageWhenEmpty
			? { left: 0, top: 0, right: nw, bottom: nh }
			: null;
	}

	const bounds: AxisBounds | null = computeContentBounds(sampled.imageData, autoTrim);

	if (!bounds) {
		return fullImageWhenEmpty
			? { left: 0, top: 0, right: nw, bottom: nh }
			: null;
	}

	const scaled: AxisBounds | null = scaleBoundsToNatural(
		bounds,
		nw,
		nh,
		sampled.analysisWidth,
		sampled.analysisHeight,
		autoTrim.edgePadding,
		minContentDimPx
	);

	if (scaled) {
		return scaled;
	}

	return fullImageWhenEmpty
		? { left: 0, top: 0, right: nw, bottom: nh }
		: null;
}

/**
 * Downscales the image into an offscreen canvas and extracts `ImageData` for trim analysis.
 * This is a best-effort operation and may return `null` (e.g. tainted canvas / browser limits).
 */
export function sampleImageForTrimAnalysis(
	img: HTMLImageElement,
	cfg: PhotoEditorAutoTrimConfig = PHOTO_EDITOR_CONFIG.autoTrim
): TrimAnalysisSample | null {
	try {
		const nw: number = img.naturalWidth;
		const nh: number = img.naturalHeight;

		const maxEdge: number = cfg.maxAnalysisEdge;

		let aw: number = nw;
		let ah: number = nh;

		if (Math.max(nw, nh) > maxEdge) {
			const r: number = maxEdge / Math.max(nw, nh);

			aw = Math.max(1, Math.round(nw * r));
			ah = Math.max(1, Math.round(nh * r));
		}

		const canvas: HTMLCanvasElement = document.createElement('canvas');

		canvas.width = aw;
		canvas.height = ah;

		const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d', {
			willReadFrequently: true
		});

		if (!ctx) {
			return null;
		}

		ctx.drawImage(img, 0, 0, aw, ah);

		return {
			imageData: ctx.getImageData(0, 0, aw, ah),
			analysisWidth: aw,
			analysisHeight: ah
		};
	}
	catch {
		return null;
	}
}

/**
 * Scans `ImageData` and returns the smallest rectangle that contains non-background pixels.
 * Background is treated as transparent pixels and (optionally) near-white opaque pixels.
 */
export function computeContentBounds(
	imageData: ImageData,
	cfg: PhotoEditorAutoTrimConfig = PHOTO_EDITOR_CONFIG.autoTrim
): AxisBounds | null {
	const {
		width,
		height,
		data: d
	}: ImageData = imageData;

	const {
		alphaEmpty: aCut,
		nearWhite: wCut,
		minOpaqueAlpha: opaque
	}: PhotoEditorAutoTrimConfig = cfg;

	const isBackground: (i: number) => boolean = (i: number): boolean => {
		const a: number = d[i + 3];

		if (a <= aCut) {
			return true;
		}

		if (a >= opaque) {
			const r: number = d[i];
			const g: number = d[i + 1];
			const b: number = d[i + 2];

			if (r >= wCut && g >= wCut && b >= wCut) {
				return true;
			}
		}

		return false;
	};

	let minX: number = width;
	let minY: number = height;
	let maxX: number = -1;
	let maxY: number = -1;

	for (let y: number = 0; y < height; y++) {
		const row: number = y * width * 4;

		for (let x: number = 0; x < width; x++) {
			const i: number = row + x * 4;

			if (!isBackground(i)) {
				if (x < minX) minX = x;
				if (y < minY) minY = y;
				if (x > maxX) maxX = x;
				if (y > maxY) maxY = y;
			}
		}
	}

	if (maxX < minX || maxY < minY) {
		return null;
	}

	return {
		left: minX,
		top: minY,
		right: maxX + 1,
		bottom: maxY + 1
	};
}

/**
 * Maps analysis-space bounds back to natural image coordinates, applies padding and minimum-size guards.
 * Returns `null` when the bounds are too small or effectively cover the full image.
 */
export function scaleBoundsToNatural(
	bounds: AxisBounds,
	nw: number,
	nh: number,
	analysisWidth: number,
	analysisHeight: number,
	pad: number,
	minContentDim: number = PHOTO_EDITOR_CONFIG.layout.minContentDimPx
): AxisBounds | null {
	const scaleX: number = nw / analysisWidth;
	const scaleY: number = nh / analysisHeight;

	const left: number = Math.max(
		0,
		Math.floor(bounds.left * scaleX) - pad
	);

	const top: number = Math.max(
		0,
		Math.floor(bounds.top * scaleY) - pad
	);

	const right: number = Math.min(
		nw,
		Math.ceil(bounds.right * scaleX) + pad
	);

	const bottom: number = Math.min(
		nh,
		Math.ceil(bounds.bottom * scaleY) + pad
	);

	const tw: number = right - left;
	const th: number = bottom - top;

	if (tw < minContentDim || th < minContentDim) {
		return null;
	}

	if (
		left <= 0 &&
		top <= 0 &&
		right >= nw &&
		bottom >= nh
	) {
		return null;
	}

	return {
		left,
		top,
		right,
		bottom
	};
}

/**
 * Converts a natural-image rectangle into Cropper selection space, using the current image transform matrix.
 * Returns `null` if the transform is missing/invalid or the resulting rectangle is too small.
 */
export function mapNaturalRectToCanvasSelectionSpace(
	cropImgHost: HTMLElement & {
		$getTransform?: () => number[];
	},
	nw: number,
	nh: number,
	left: number,
	top: number,
	right: number,
	bottom: number,
	minRectPx: number = PHOTO_EDITOR_CONFIG.layout.minCropRectPx
): CanvasSelectionRect | null {
	const m: number[] | undefined = cropImgHost.$getTransform?.();

	if (!m || m.length < 6) {
		return null;
	}

	const a: number = m[0];
	const bm: number = m[1];
	const c: number = m[2];
	const d: number = m[3];
	const e: number = m[4];
	const f: number = m[5];

	const cx: number = nw / 2;
	const cy: number = nh / 2;

	const ox: number = cropImgHost.offsetLeft;
	const oy: number = cropImgHost.offsetTop;

	const mapPoint: (ix: number, iy: number) => VectorAxis = (
		ix: number,
		iy: number
	): VectorAxis => ({
		x: ox + a * (ix - cx) + c * (iy - cy) + e + cx,
		y: oy + bm * (ix - cx) + d * (iy - cy) + f + cy
	});

	const corners: readonly VectorAxis[] = [
		mapPoint(left, top),
		mapPoint(right, top),
		mapPoint(left, bottom),
		mapPoint(right, bottom)
	];

	const xs: number[] = corners.map(
		(point: VectorAxis): number => point.x
	);

	const ys: number[] = corners.map(
		(point: VectorAxis): number => point.y
	);

	const sx: number = Math.min(...xs);
	const sy: number = Math.min(...ys);

	const sw: number = Math.max(...xs) - sx;
	const sh: number = Math.max(...ys) - sy;

	if (sw < minRectPx || sh < minRectPx) {
		return null;
	}

	return {
		sx,
		sy,
		sw,
		sh
	};
}

/**
 * Finds the largest rectangle with a given aspect ratio that fits inside the provided bounds.
 */
export function maxInscribedRectWithAspectRatio(
	box: AxisBounds,
	widthOverHeight: number
): AxisBounds | null {
	if (
		!Number.isFinite(widthOverHeight) ||
		widthOverHeight <= 0
	) {
		return null;
	}

	const maxW: number = box.right - box.left;
	const maxH: number = box.bottom - box.top;

	if (maxW <= 0 || maxH <= 0) {
		return null;
	}

	const rw: number = maxW / maxH;

	let w: number;
	let h: number;

	if (rw >= widthOverHeight) {
		h = maxH;
		w = h * widthOverHeight;
	} else {
		w = maxW;
		h = w / widthOverHeight;
	}

	const left: number = box.left + (maxW - w) / 2;
	const top: number = box.top + (maxH - h) / 2;

	return {
		left,
		top,
		right: left + w,
		bottom: top + h
	};
}

/**
 * Computes the maximum crop rectangle (in canvas CSS pixels) for a given aspect ratio.
 * Returns `null` when the canvas is too small or the rectangle can't satisfy `minRectPx`.
 */
export function maxCropSelectionInCanvasCssPixels(
	canvasCssWidth: number,
	canvasCssHeight: number,
	widthOverHeight: number,
	minRectPx: number
): CanvasSelectionRect | null {
	if (
		canvasCssWidth <= 0 ||
		canvasCssHeight <= 0 ||
		!Number.isFinite(widthOverHeight) ||
		widthOverHeight <= 0
	) {
		return null;
	}

	const bounds: AxisBounds = {
		left: 0,
		top: 0,
		right: canvasCssWidth,
		bottom: canvasCssHeight
	};

	const inscribed: AxisBounds | null =
		maxInscribedRectWithAspectRatio(
			bounds,
			widthOverHeight
		);

	if (!inscribed) {
		return null;
	}

	const sx: number = inscribed.left;
	const sy: number = inscribed.top;

	const sw: number = inscribed.right - inscribed.left;
	const sh: number = inscribed.bottom - inscribed.top;

	if (sw < minRectPx || sh < minRectPx) {
		return null;
	}

	return {
		sx,
		sy,
		sw,
		sh
	};
}

const ALPHA_SAMPLE_STRIDE: number = 4;

/**
 * Checks whether the canvas contains any non-opaque pixels.
 * Used to pick PNG output for transparency preservation.
 */
export function canvasHasTransparency(
	canvas: HTMLCanvasElement
): boolean {
	const w: number = canvas.width;
	const h: number = canvas.height;

	if (w <= 0 || h <= 0) {
		return false;
	}

	const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d', {
		willReadFrequently: true
	});

	if (!ctx) {
		return false;
	}

	try {
		const imageData: ImageData = ctx.getImageData(0, 0, w, h);
		const data: Uint8ClampedArray = imageData.data;

		for (let y: number = 0; y < h; y += ALPHA_SAMPLE_STRIDE) {
			const row: number = y * w * 4;

			for (let x: number = 0; x < w; x += ALPHA_SAMPLE_STRIDE) {
				if (data[row + x * 4 + 3] < 255) {
					return true;
				}
			}
		}

		return false;
	}
	catch {
		// Cross-origin image data taints canvas and blocks pixel reads.
		return false;
	}
}

export interface PhotoEditorExportCanvasSize {
	readonly width: number;
	readonly height: number;
}

/**
 * Maps the current crop selection from CSS pixels to the highest useful export resolution
 * supported by the source image (same density as natural pixels visible in the selection).
 */
export function resolvePhotoEditorExportCanvasSize(
	cropImg: Parameters<typeof mapNaturalRectToCanvasSelectionSpace>[0],
	selection: { width: number; height: number },
	naturalWidth: number,
	naturalHeight: number,
	maxEdgePx: number
): PhotoEditorExportCanvasSize | null {
	const selectionWidth: number = selection.width;
	const selectionHeight: number = selection.height;

	if (
		selectionWidth <= 0 ||
		selectionHeight <= 0 ||
		naturalWidth <= 0 ||
		naturalHeight <= 0 ||
		maxEdgePx <= 0
	) {
		return null;
	}

	const fullImageBox: CanvasSelectionRect | null = mapNaturalRectToCanvasSelectionSpace(
		cropImg,
		naturalWidth,
		naturalHeight,
		0,
		0,
		naturalWidth,
		naturalHeight,
		1
	);

	if (!fullImageBox || fullImageBox.sw <= 0 || fullImageBox.sh <= 0) {
		return null;
	}

	const scaleX: number = naturalWidth / fullImageBox.sw;
	const scaleY: number = naturalHeight / fullImageBox.sh;
	const uniformScale: number = Math.min(scaleX, scaleY);

	let width: number = Math.max(Math.round(selectionWidth * uniformScale), Math.round(selectionWidth));
	let height: number = Math.max(Math.round(selectionHeight * uniformScale), Math.round(selectionHeight));

	const longestEdge: number = Math.max(width, height);
	if (longestEdge > maxEdgePx) {
		const ratio: number = maxEdgePx / longestEdge;
		width = Math.max(1, Math.round(width * ratio));
		height = Math.max(1, Math.round(height * ratio));
	}

	return { width, height };
}

/**
 * Serializes the edited canvas to a data URL.
 * Uses PNG when transparency is present; otherwise uses JPEG with the provided quality.
 */
export function canvasToPhotoEditorDataUrl(
	canvas: HTMLCanvasElement,
	jpegQuality: number
): string | undefined {
	try {
		if (canvasHasTransparency(canvas)) {
			return canvas.toDataURL('image/png');
		}

		return canvas.toDataURL('image/jpeg', jpegQuality);
	}
	catch {
		try {
			return canvas.toDataURL('image/png');
		}
		catch {
			return undefined;
		}
	}
}

function centeredBounds(
	width: number,
	height: number
): AxisBounds {
	return {
		left: -width / 2,
		right: width / 2,
		top: -height / 2,
		bottom: height / 2
	};
}

function handleLocalPoint(
	handle: PhotoEditorStretchHandle,
	width: number,
	height: number
): VectorAxis {
	const b: AxisBounds = centeredBounds(width, height);

	switch (handle) {
		case 'n':
			return { x: 0, y: b.top };

		case 's':
			return { x: 0, y: b.bottom };

		case 'e':
			return { x: b.right, y: 0 };

		case 'w':
			return { x: b.left, y: 0 };

		case 'ne':
			return { x: b.right, y: b.top };

		case 'nw':
			return { x: b.left, y: b.top };

		case 'se':
			return { x: b.right, y: b.bottom };

		case 'sw':
			return { x: b.left, y: b.bottom };
	}
}

function oppositeHandle(
	handle: PhotoEditorStretchHandle
): PhotoEditorStretchHandle {
	switch (handle) {
		case 'n':
			return 's';

		case 's':
			return 'n';

		case 'e':
			return 'w';

		case 'w':
			return 'e';

		case 'ne':
			return 'sw';

		case 'nw':
			return 'se';

		case 'se':
			return 'nw';

		case 'sw':
			return 'ne';
	}
}

function mapCenteredLocalToCanvas(
	transform: TransformMatrix2D,
	frame: PhotoEditorImageFrame,
	point: VectorAxis
): VectorAxis {
	return {
		x:
			frame.offsetLeft +
			transform.a * point.x +
			transform.c * point.y +
			transform.e +
			frame.width / 2,

		y:
			frame.offsetTop +
			transform.b * point.x +
			transform.d * point.y +
			transform.f +
			frame.height / 2
	};
}

function safeDiv(v: number, base: number): number {
	if (Math.abs(base) < 1e-6) {
		return 1;
	}

	return v / base;
}

/**
 * Resolves canvas-space coordinates for each stretch handle based on the current image transform.
 */
export function resolveStretchHandles(
	transform: TransformMatrix2D,
	frame: PhotoEditorImageFrame
): PhotoEditorStretchHandlePoint[] {
	const handles: readonly PhotoEditorStretchHandle[] = [
		'n',
		's',
		'e',
		'w',
		'ne',
		'nw',
		'se',
		'sw'
	];

	return handles.map(
		(
			handle: PhotoEditorStretchHandle
		): PhotoEditorStretchHandlePoint => {
			const local: VectorAxis = handleLocalPoint(
				handle,
				frame.width,
				frame.height
			);

			const world: VectorAxis = mapCenteredLocalToCanvas(
				transform,
				frame,
				local
			);

			return {
				handle,
				x: world.x,
				y: world.y
			};
		}
	);
}

/**
 * Calculates a new image transform matrix after dragging a stretch handle.
 * The dragged handle moves in screen space, while the opposite handle is treated as an anchor.
 * Returns `null` if the transform can't be inverted or if the result would be invalid.
 */
export function transformAfterStretchDrag(
	input: PhotoEditorStretchDragInput
): TransformMatrix2D | null {
	const {
		transform,
		frame,
		minScale,
		handle,
		startPointerX,
		startPointerY,
		pointerX,
		pointerY
	}: PhotoEditorStretchDragInput = input;

	const dragDx: number = pointerX - startPointerX;
	const dragDy: number = pointerY - startPointerY;

	const handleLocal: VectorAxis = handleLocalPoint(
		handle,
		frame.width,
		frame.height
	);

	const anchorLocal: VectorAxis = handleLocalPoint(
		oppositeHandle(handle),
		frame.width,
		frame.height
	);

	const startHandleWorld: VectorAxis = mapCenteredLocalToCanvas(
		transform,
		frame,
		handleLocal
	);

	const anchorWorld: VectorAxis = mapCenteredLocalToCanvas(
		transform,
		frame,
		anchorLocal
	);

	const targetHandleWorld: VectorAxis = {
		x: startHandleWorld.x + dragDx,
		y: startHandleWorld.y + dragDy
	};

	const targetDeltaWorld: VectorAxis = {
		x: targetHandleWorld.x - anchorWorld.x,
		y: targetHandleWorld.y - anchorWorld.y
	};

	const det: number =
		transform.a * transform.d -
		transform.b * transform.c;

	if (Math.abs(det) < 1e-9) {
		return null;
	}

	const inv: Matrix2D = {
		a: transform.d / det,
		b: -transform.b / det,
		c: -transform.c / det,
		d: transform.a / det
	};

	const targetDeltaLocal: VectorAxis = {
		x:
			inv.a * targetDeltaWorld.x +
			inv.c * targetDeltaWorld.y,

		y:
			inv.b * targetDeltaWorld.x +
			inv.d * targetDeltaWorld.y
	};

	const baseDeltaLocal: VectorAxis = {
		x: handleLocal.x - anchorLocal.x,
		y: handleLocal.y - anchorLocal.y
	};

	let sx: number = safeDiv(
		targetDeltaLocal.x,
		baseDeltaLocal.x
	);

	let sy: number = safeDiv(
		targetDeltaLocal.y,
		baseDeltaLocal.y
	);

	if (baseDeltaLocal.x === 0) {
		sx = 1;
	}

	if (baseDeltaLocal.y === 0) {
		sy = 1;
	}

	sx = Math.max(sx, minScale);
	sy = Math.max(sy, minScale);

	if (!Number.isFinite(sx) || !Number.isFinite(sy)) {
		return null;
	}

	const tx: number = anchorLocal.x * (1 - sx);
	const ty: number = anchorLocal.y * (1 - sy);

	return {
		a: transform.a * sx,
		b: transform.b * sx,
		c: transform.c * sy,
		d: transform.d * sy,
		e:
			transform.a * tx +
			transform.c * ty +
			transform.e,
		f:
			transform.b * tx +
			transform.d * ty +
			transform.f
	};
}

export function asPhotoEditorCropper(cropper: Cropper | null): PhotoEditorCropperFacade | null {
	return cropper ? (cropper as unknown as PhotoEditorCropperFacade) : null;
}

export function cropperZoomArgumentForUniformScale(multiplier: number): number {
	if (!Number.isFinite(multiplier) || multiplier <= 0) return 0;
	return multiplier >= 1 ? multiplier - 1 : 1 - 1 / multiplier;
}

export function resolveMatrixScale(
	a: number,
	b: number,
	c: number,
	d: number
): MatrixScale | null {
	if (![a, b, c, d].every(Number.isFinite)) {
		return null;
	}

	return {
		scaleX: Math.hypot(a, b),
		scaleY: Math.hypot(c, d)
	};
}